import type { Guide } from "./types.js";
import { guide as gettingStarted } from "./getting-started.js";
import { guide as produceArticle } from "./produce-article.js";
import { guide as distributeArticle } from "./distribute-article.js";
import { guide as publishSanity } from "./publish-sanity.js";
import { guide as regenerateArticle } from "./regenerate-article.js";
import { guide as viralVideos } from "./viral-videos.js";
import { guide as clusterPlanner } from "./cluster-planner.js";
import { guide as clusterExpansion } from "./cluster-expansion.js";
import { guide as contentDb } from "./content-db.js";
import { guide as entities } from "./entities.js";
import { guide as assetStorage } from "./asset-storage.js";
import { guide as errorLog } from "./error-log.js";
import { guide as spendDashboard } from "./spend-dashboard.js";
import { guide as brandKit } from "./brand-kit.js";
import { guide as backups } from "./backups.js";
import { guide as gscSearchConsole } from "./gsc-search-console.js";
import { guide as taskManagement } from "./task-management.js";
import { guide as costOptimization } from "./cost-optimization.js";
import { guide as troubleshooting } from "./troubleshooting.js";
import { guide as credentialsReference } from "./credentials-reference.js";
import { guide as devArchitecture } from "./dev-architecture.js";
import { guide as devAddChannel } from "./dev-add-channel.js";
import { guide as devAddIntegration } from "./dev-add-integration.js";
import { guide as devConventions } from "./dev-conventions.js";

/**
 * Ordered registry of all help guides. Order in this array determines the
 * order they appear in the sidebar tree (grouped by category).
 *
 * Adding a new guide:
 *   1. Create `guides/<id>.tsx` with `export const guide: Guide = { meta, Body }`
 *   2. Import it here + add to the array
 *   3. Done — the registry rebuilds itself from each guide's metadata
 */
export const GUIDES: Guide[] = [
  gettingStarted,
  produceArticle,
  publishSanity,
  regenerateArticle,
  viralVideos,
  distributeArticle,
  clusterPlanner,
  clusterExpansion,
  contentDb,
  entities,
  assetStorage,
  errorLog,
  spendDashboard,
  brandKit,
  backups,
  gscSearchConsole,
  taskManagement,
  costOptimization,
  troubleshooting,
  credentialsReference,
  devArchitecture,
  devAddChannel,
  devAddIntegration,
  devConventions,
];

export const CATEGORY_ORDER: Guide["meta"]["category"][] = [
  "Getting started",
  "Workflows",
  "Reference",
  "Troubleshooting",
  "For developers",
];

export function findGuide(id: string): Guide | undefined {
  return GUIDES.find((g) => g.meta.id === id);
}

export function guidesByCategory(): { category: Guide["meta"]["category"]; guides: Guide[] }[] {
  return CATEGORY_ORDER
    .map((cat) => ({ category: cat, guides: GUIDES.filter((g) => g.meta.category === cat) }))
    .filter((g) => g.guides.length > 0);
}
